import { COMMAND_CENTER_ROOT, commandCenterPath } from './commandCenterRoutes'
import { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import ProjectDetailView from './projects/ProjectDetailView'
import { getPortal, getPortalContext, humanizePortalError, type PortalDetail, type PortalContextRecord } from './olivium/portal-factory/portalFactory'
import { getDivisionWorkspace } from './workspaces/divisionWorkspaces'
import { isAuthError, ApiError } from '../../api/client'

/**
 * /square/command-center/projects/:projectId — route wrapper (PHASE_2M).
 *
 * Division ids resolve statically (no fetch); anything else is treated as a
 * live portal id and loaded from the real backend. 404 → honest not-found.
 * Static /projects/arbuckle ranks above this route and keeps working.
 */
export default function SquareCodeProjectDetailRoute() {
  const navigate = useNavigate()
  const { projectId = '' } = useParams()
  const isDivision = !!getDivisionWorkspace(projectId)
  const [state, setState] = useState<{ detail: PortalDetail | null; context: PortalContextRecord | null; loading: boolean; error: string | null; authRequired: boolean; notFound: boolean }>(
    { detail: null, context: null, loading: !isDivision, error: null, authRequired: false, notFound: false },
  )

  useEffect(() => {
    if (isDivision || !projectId) return
    const ac = new AbortController()
    ;(async () => {
      try {
        const [detail, context] = await Promise.all([
          getPortal(projectId, ac.signal),
          getPortalContext(projectId, ac.signal).catch(() => null),
        ])
        if (!ac.signal.aborted) setState({ detail, context, loading: false, error: null, authRequired: false, notFound: false })
      } catch (err) {
        if (ac.signal.aborted) return
        if (isAuthError(err)) setState((s) => ({ ...s, loading: false, authRequired: true }))
        else if (err instanceof ApiError && err.status === 404) setState((s) => ({ ...s, loading: false, notFound: true }))
        else setState((s) => ({ ...s, loading: false, error: humanizePortalError(err) }))
      }
    })()
    return () => ac.abort()
  }, [projectId, isDivision])

  return (
    <ProjectDetailView
      projectId={projectId}
      detail={state.detail}
      context={state.context}
      loading={state.loading}
      error={state.error}
      authRequired={state.authRequired}
      notFound={state.notFound}
      onOpen={(path) => navigate(path)}
      onHome={() => navigate(COMMAND_CENTER_ROOT)}
      onBackCommandCenter={() => navigate(COMMAND_CENTER_ROOT)}
      onBackProjects={() => navigate(commandCenterPath('projects'))}
    />
  )
}
